import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import {
  DrugActionTypes,
  DrugsPageRequested,
  DrugsPageLoaded,
  DrugCreated,
  DrugDeleted,
  DrugUpdated,
  DrugOnServerCreated,
  DrugsActionToggleLoading,
  DrugsPageToggleLoading,
  ThrowDrugError,
  FireDrugSuccess,
  DrugOnServerUpdated,
  DrugsPageCancelled,
  DrugOnServerDeleted,
} from '../_actions/drug.actions';
import { Router } from '@angular/router';
import { DrugService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class DrugEffects {
  showPageLoadingDistpatcher = new DrugsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new DrugsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new DrugsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new DrugsActionToggleLoading({ isLoading: false });

  @Effect()
  loadDrugsPage$ = this.actions$.pipe(
    ofType<DrugsPageRequested>(DrugActionTypes.DrugsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.drug.findDrugs(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new DrugsPageCancelled();
            } else {
              this.store.dispatch(new ThrowDrugError({ error: response[0].error.error }));
              return new DrugsPageCancelled();
            }
          } else {
            const result = response[0];
            return new DrugsPageLoaded({
              drugs: result.data,
              totalCount: result.count,
              page: response[1],
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteDrug$ = this.actions$.pipe(
    ofType<DrugOnServerDeleted>(DrugActionTypes.DrugOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drug.deleteDrug(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(new FireDrugSuccess({ action_type: DrugActionTypes.DrugDeleted }));
            return new DrugDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateDrug$ = this.actions$.pipe(
    ofType<DrugOnServerUpdated>(DrugActionTypes.DrugOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drug.updateDrug(payload.drug).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(new FireDrugSuccess({ action_type: DrugActionTypes.DrugUpdated }));
            return new DrugUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createDrug$ = this.actions$.pipe(
    ofType<DrugOnServerCreated>(DrugActionTypes.DrugOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drug.createDrug(payload.drug).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugError({ error: res.error });
          } else {
            this.store.dispatch(new FireDrugSuccess({ action_type: DrugActionTypes.DrugCreated }));
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new DrugCreated({ drug: res.data });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private drug: DrugService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
